import {useState} from 'react'
import {fetchCommunitySponsorAssignments} from '../api/sponsorAssignments'
import {AppState} from '../store/store'
import {LessonSponsorAssignment} from '../types/sponsorAssignments'

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: (assignments: LessonSponsorAssignment[]) => void
}>

type UseCommunitySponsorAssignmentsReturn = {
  assignments: LessonSponsorAssignment[]
  isLoading: boolean
  error: Error | null
  fetchAssignments: (communityId: string, state: AppState) => void
  clearAssignments: () => void
  updateAssignments: (assignments: LessonSponsorAssignment[]) => void
}

export function useCommunitySponsorAssignments({
  onError,
  onSuccess
}: Options = {}): UseCommunitySponsorAssignmentsReturn {
  const [assignments, setAssignments] = useState<LessonSponsorAssignment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchAssignments = (communityId: string, state: AppState): void => {
    setIsLoading(true)
    setError(null)

    fetchCommunitySponsorAssignments(communityId, state)
      .then(result => {
        setAssignments(result.assignments)
        onSuccess?.(result.assignments)
      })
      .catch(err => {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch community sponsor assignments')
        setError(error)
        onError?.(error)
      })
      .finally(() => setIsLoading(false))
  }

  const clearAssignments = (): void => {
    setAssignments([])
    setError(null)
  }

  const updateAssignments = (
    newAssignments: LessonSponsorAssignment[]
  ): void => {
    setAssignments(newAssignments)
  }

  return {
    assignments,
    isLoading,
    error,
    fetchAssignments,
    clearAssignments,
    updateAssignments
  }
}
